import { TOTAL_WIN_COUNTUP_DURATION, WinLabel } from '@/containers/winnings/WinLabel'
import { Orientation, ShowRegularWinningsData, WinCombination } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import GeneralSignals from '@/signals/GeneralSignals'
import { BaseContainer } from '@base/base/BaseContainer'
import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import Facade from '@/Facade'
import { WinLabelsPool } from '@/factories/Factory'
import gsap from 'gsap'
import { Payline } from '@/containers/winnings/Payline'
import * as PIXI from 'pixi.js'

export class ShowWinnings extends BaseContainer {
    private static readonly PAYLINE_MESH_BASE_OFFSET_X_LANDSCAPE: number = 590
    private static readonly PAYLINE_MESH_BASE_OFFSET_Y_LANDSCAPE: number = 157
    private static readonly PAYLINE_MESH_BASE_OFFSET_X_PORTRAIT: number = 90
    private static readonly PAYLINE_MESH_BASE_OFFSET_Y_PORTRAIT: number = 694
    private static readonly PAYLINE_PATH_SCALE_X_LANDSCAPE: number = 1
    private static readonly PAYLINE_PATH_SCALE_Y_LANDSCAPE: number = 1
    private static readonly PAYLINE_PATH_SCALE_X_PORTRAIT: number = 0.92
    private static readonly PAYLINE_PATH_SCALE_Y_PORTRAIT: number = 0.9
    /** Payline + win anims visible. */
    private static readonly LINE_BY_LINE_SHOW_MS: number = 1500
    /** Gap with no payline and no win anims before the next line. */
    private static readonly LINE_BY_LINE_PAUSE_MS: number = 200
    private static readonly DEBUG_OFFSET_STEP: number = 2
    private static readonly DEFAULT_DEBUG_OFFSET_X: number = 0
    private static readonly DEFAULT_DEBUG_OFFSET_Y: number = 0
    private static readonly TOTAL_WIN_SHOW_DELAY: number = 0.15
    /** Hold after count-up completes before fade-out starts. */
    private static readonly TOTAL_WIN_HOLD_AFTER_COUNTUP: number = 0.75
    /** After ModulesManager's settled resize (120ms) so offsets match the new reel frame. */
    private static readonly PAYLINE_LAYOUT_SETTLE_S: number = 0.14
    private static readonly DEBUG_PAYLINES_20: number[][] = [
        [0, 0, 0, 0, 0],
        [1, 1, 1, 1, 1],
        [2, 2, 2, 2, 2],
        [0, 1, 2, 1, 0],
        [2, 1, 0, 1, 2],
        [0, 0, 1, 2, 2],
        [2, 2, 1, 0, 0],
        [1, 0, 0, 0, 1],
        [1, 2, 2, 2, 1],
        [0, 1, 1, 1, 0],
        [2, 1, 1, 1, 2],
        [1, 0, 1, 2, 1],
        [1, 2, 1, 0, 1],
        [0, 1, 0, 1, 0],
        [2, 1, 2, 1, 2],
        [1, 0, 1, 0, 1],
        [1, 2, 1, 2, 1],
        [0, 0, 0, 1, 2],
        [2, 2, 2, 1, 0],
        [2, 1, 0, 0, 0],
    ]
    private _activeLabels: WinLabel[]
    private _activeTotalWin: number
    private _activeLines: Payline[]
    private _lineByLineCombinations: WinCombination[]
    private _lineByLineIndex: number
    private _lineByLineTimer: ReturnType<typeof setTimeout> | null
    private _lineByLineSoundPlayedLines: Set<string>
    private _isNativeLineByLineActive: boolean
    private _isLocalLineByLineActive: boolean
    /** Payline loop hidden while commendation / big-win overlay is up. */
    private _isLineByLineSuspendedForBigWin: boolean
    private _debugOffsetX: number
    private _debugOffsetY: number
    private _debugControls: PIXI.Container
    private _debugOffsetText: PIXI.Text
    private _debugKeywordText: PIXI.Text
    private _isShowingDebugLines: boolean
    private _debugLineNumberLabels: PIXI.Text[]
    private _lineByLineLabel: WinLabel | null
    private _debugActiveLineNumber: number
    private _drawnCombinations: WinCombination[]
    private _appliedPaylineOrientation: string | null
    private _paylineLayoutSyncCall: gsap.core.Tween | null

    public constructor() {
        super()

        GameSignals.showWinLabels.connect(this.onShowWinLabels)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GeneralSignals.orientationChanged.connect(this.onOrientationChanged)
        ;(GameSignals as any).featureIntroRequested?.connect(this.onFeatureIntroRequested)
        ;(GameSignals as any).featureIntroContinue?.connect(this.onFeatureIntroContinue)
        ;(GameSignals as any).showWinningsComplete?.connect(this.onShowWinningsComplete)
        ;(GameSignals as any).startSpin?.connect(this.onStopLineByLine)
        ;(GameSignals as any).stopButtonClicked?.connect(this.onSkipLineByLine)
        ;(GameSignals as any).spinButtonClicked?.connect(this.onSkipLineByLine)
        ;(GameSignals as any).changeBet?.connect(this.onBetChanged)
        ;(GameSignals as any).settingsMenuOpened?.connect(this.onBetChanged)
        ;(GameSignals as any).openBuyBonusPopup?.connect(this.onBetChanged)
        ;(GameSignals as any).showLineByLine?.connect(this.onShowLineByLine)
        ;(GameSignals as any).showLineByLineComplete?.connect(this.onShowLineByLineComplete)
        ;(GameSignals as any).showLineByLineSkipped?.connect(this.onShowLineByLineComplete)
        GameSignals.bigWinPresentation.connect(this.onBigWinPresentation)
        GameSignals.showBigWinComplete.connect(this.onBigWinPresentationComplete)
        GameSignals.activateScatters.connect(this.onStopLineByLine)

        this._activeLabels = []
        this._activeTotalWin = 0
        this._activeLines = []
        this._lineByLineCombinations = []
        this._lineByLineIndex = 0
        this._lineByLineTimer = null
        this._lineByLineSoundPlayedLines = new Set<string>()
        this._isNativeLineByLineActive = false
        this._isLocalLineByLineActive = false
        this._isLineByLineSuspendedForBigWin = false
        this._debugOffsetX = ShowWinnings.DEFAULT_DEBUG_OFFSET_X
        this._debugOffsetY = ShowWinnings.DEFAULT_DEBUG_OFFSET_Y
        this._isShowingDebugLines = false
        this._debugLineNumberLabels = []
        this._lineByLineLabel = null
        this._debugActiveLineNumber = 0
        this._drawnCombinations = []
        this._appliedPaylineOrientation = null
        this._paylineLayoutSyncCall = null

        this.applyCurrentMeshOffset()

        // Winline debugger disabled (can be restored by uncommenting these lines).
        // this.createDebugControls()
        // this.refreshDebugOffsetText()
        // this.refreshDebugKeywordText()
        // ;(globalThis as any).__setWinlineKeyword = (keyword: string) => this.applyDebugKeyword(keyword)
    }

    private onShowRegularWin = (data: ShowRegularWinningsData) => {
        this.clearLineByLineTimer()
        this._lineByLineCombinations = [...(data.combinations || [])]
        this._lineByLineIndex = 0
        this._lineByLineSoundPlayedLines.clear()
        this._isNativeLineByLineActive = false
        this._isLocalLineByLineActive = false
    }

    private onFeatureIntroRequested = () => {
        gsap.killTweensOf(this)
        gsap.to(this, {
            duration: 0.35,
            alpha: 0,
        })
    }

    private onFeatureIntroContinue = () => {
        gsap.killTweensOf(this)
        gsap.to(this, {
            duration: 0.25,
            alpha: 1,
        })
    }

    private shouldSkipLineByLineForFreeSpinsIntro(): boolean {
        const model = Facade.model
        const freeSpinsAwarded = Number(model?.spinData?.bonus?.freebet_given || 0)

        return freeSpinsAwarded > 0 && !model?.roundState?.isFreeSpins
    }

    private isAutoplayActive(): boolean {
        return !!Facade.isAuto
    }

    private isFreeSpinsActive(): boolean {
        return !!Facade.model?.roundState?.isFreeSpins
    }

    private shouldSkipLineByLine(): boolean {
        return this.isAutoplayActive() || this.isFreeSpinsActive() || this.shouldSkipLineByLineForFreeSpinsIntro()
    }

    /** StopSpinState resolves bigWinLevel before FinalState walks its children, so the total-win
     * label already knows whether BigWinState will take over once RegularWinningsState is done. */
    private isCommendationPending(): boolean {
        return Facade.model?.bigWinLevel != null
    }

    private onShowWinLabels = (winCombinations: WinCombination[]) => {
        if (this._isShowingDebugLines) {
            return
        }

        this.hideLineByLineLabel()
        this.clearActiveLines()
        this._drawnCombinations = [...winCombinations]

        winCombinations.forEach((combination: WinCombination) => {
            this.drawLineForCombination(combination)
        })

        const totalWin = winCombinations.reduce((sum, combination) => {
            return sum + Number(combination.win_currency || 0)
        }, 0)
        this._activeTotalWin = totalWin

        if (totalWin > 0 && !this.isCommendationPending()) {
            const label: WinLabel = WinLabelsPool.out(this)
            this._activeLabels.push(label)

            //## Total win ..!
            label.setVariant('total_win')
            const centerX = ReelsContainerHelper.calculateContainerWidth() / 2
            const centerY = ReelsContainerHelper.calculateContainerHeight() / 2
            label.position.set(centerX, centerY)
            label.visible = false

            gsap.to(label, {
                duration: ShowWinnings.TOTAL_WIN_SHOW_DELAY,
                onComplete: () => {
                    label.countUpValue(totalWin, TOTAL_WIN_COUNTUP_DURATION)
                    label.showLabel()
                    
                    //## Total win Sound Signal ..!
                    ;(GameSignals as any).showTotalWinLabel?.emit(totalWin)
                },
            })
        }

        const hideDelay =
            ShowWinnings.TOTAL_WIN_SHOW_DELAY + TOTAL_WIN_COUNTUP_DURATION + ShowWinnings.TOTAL_WIN_HOLD_AFTER_COUNTUP

        gsap.to(this, {
            duration: hideDelay,
            onComplete: () => {
                this._activeLabels.forEach((label: WinLabel) => {
                    label.hideLabel(() => {
                        WinLabelsPool.in(label)
                    })
                })

                this.clearActiveLines()
                this._drawnCombinations = []
                this._activeLabels = []
                this._activeTotalWin = 0
            },
        })
    }

    private drawLineForCombination(combination: WinCombination, staticDisplay: boolean = false): void {
        const hasReliablePaylineNumber = Number(combination?.payline_number || 0) > 0
        const lineSource =
            !hasReliablePaylineNumber && Array.isArray(combination.payline_path) && combination.payline_path.length >= 2
                ? combination.payline_path
                : combination.symbols_positions

        const rawPoints = (lineSource || [])
            .filter((position) => Array.isArray(position) && position.length >= 2)
            .slice()
            .sort((a, b) => a[0] - b[0])

        if (rawPoints.length < 2) {
            return
        }

        this.drawLineFromRawPoints(this.expandToFullPaylinePath(rawPoints, combination), staticDisplay)
    }

    private expandToFullPaylinePath(rawPoints: number[][], combination?: WinCombination): number[][] {
        const totalReels = Number(Facade.reelsConfig.TOTAL_REELS || 5)
        if (totalReels <= 1 || rawPoints.length >= totalReels) {
            return rawPoints
        }

        const paylineNumber = Number(combination?.payline_number || 0)
        const referenceLines = this.getReferencePaylines()
        if (paylineNumber > 0) {
            if (paylineNumber <= referenceLines.length) {
                const pattern = referenceLines[paylineNumber - 1]
                if (Array.isArray(pattern) && pattern.length >= totalReels) {
                    return pattern.slice(0, totalReels).map((row, reel) => [reel, row])
                }
            }

            // If server provides a payline number, do not infer another pattern.
            return rawPoints
        }

        const normalized = rawPoints
            .map((point) => [Number(point[0]), Number(point[1])])
            .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]))

        if (!normalized.length) {
            return rawPoints
        }

        const matchingPattern = referenceLines.find((pattern) => {
            if (!Array.isArray(pattern) || pattern.length < totalReels) {
                return false
            }

            return normalized.every(([reel, row]) => pattern[reel] === row)
        })

        if (matchingPattern) {
            return matchingPattern.slice(0, totalReels).map((row, reel) => [reel, row])
        }

        const rowsByReel = new Map<number, number>()
        normalized.forEach(([reel, row]) => {
            rowsByReel.set(reel, row)
        })

        const fallbackRow = normalized[normalized.length - 1][1]
        const expanded: number[][] = []

        for (let reel = 0; reel < totalReels; reel++) {
            expanded.push([reel, rowsByReel.get(reel) ?? fallbackRow])
        }

        return expanded
    }

    private drawLineFromRawPoints(rawPoints: number[][], staticDisplay: boolean = false): void {
        if (rawPoints.length < 2) {
            return
        }

        this.applyCurrentMeshOffset()
        this._appliedPaylineOrientation = String(Facade.orientation || '')

        const ropePoints = Payline.buildRopePoints(
            rawPoints,
            ReelsContainerHelper.calculateReelWidth(),
            ReelsContainerHelper.calculateReelHeight()
        )
        const orientedRopePoints = this.applyOrientationPathScale(ropePoints)

        const line = new Payline(orientedRopePoints)
        line.x = this.getPaylineOriginX()
        line.y = this.getPaylineOriginY()
        ;(this as any).addChildAt(line as any, 0)
        this._activeLines.push(line)

        if (staticDisplay) {
            line.showStatic()
            return
        }

        line.show()
    }

    private onShowLineByLine = (combinations: WinCombination[]) => {
        if (!combinations?.length) {
            return
        }

        if (this.shouldSkipLineByLine()) {
            this.onStopLineByLine()
            return
        }

        this._isNativeLineByLineActive = true
        this._isLocalLineByLineActive = false
        this._lineByLineCombinations = [...combinations]
        this._lineByLineIndex = 0
        this.startLocalLineByLineLoop()
    }

    private onShowLineByLineComplete = () => {
        const wasLoopRunning = this._isNativeLineByLineActive || this._isLocalLineByLineActive

        this._isNativeLineByLineActive = false
        this._isLocalLineByLineActive = false
        this.clearLineByLineTimer()
        this.hideLineByLineLabel()
        this.clearActiveLines()
        this._drawnCombinations = []

        //## Only announce a stop when a loop was actually running, so subscribers can trust it.
        if (wasLoopRunning) {
            ;(GameSignals as any).lineByLineLoopStopped?.emit()
        }
    }

    private onShowWinningsComplete = () => {
        if (this._isNativeLineByLineActive) {
            return
        }

        if (this.isAutoplayActive()) {
            this.onStopLineByLine()
            return
        }

        if (this.shouldSkipLineByLine()) {
            this.onStopLineByLine()
            return
        }

        if (!this._lineByLineCombinations.length) {
            return
        }

        this._isLocalLineByLineActive = true
        this.startLocalLineByLineLoop()
    }

    private onBigWinPresentation = () => {
        this.suspendLineByLineForBigWin()
    }

    private onBigWinPresentationComplete = () => {
        if (!this._isLineByLineSuspendedForBigWin) {
            return
        }

        this._isLineByLineSuspendedForBigWin = false

        // Base game: resume payline loop after commendation when it was already cycling.
        const canResumeLineByLine =
            this._lineByLineCombinations.length > 0 &&
            (this._isNativeLineByLineActive || this._isLocalLineByLineActive) &&
            !this.shouldSkipLineByLine()

        if (canResumeLineByLine) {
            this._isLocalLineByLineActive = true
            this.startLocalLineByLineLoop()
            return
        }

        if (this._isNativeLineByLineActive || this._isLocalLineByLineActive) {
            this.onStopLineByLine()
        }
    }

    /** Hide paylines / labels while commendation plays; keep combinations for base-game resume. */
    private suspendLineByLineForBigWin = () => {
        // Always clear board overlays behind commendation (total-win lines + line-by-line).
        gsap.killTweensOf(this)
        this.clearLineByLineTimer()
        this.clearActiveLines()
        this.hideLineByLineLabel()
        this._activeLabels.forEach((label: WinLabel) => {
            gsap.killTweensOf(label)
            label.visible = false
            WinLabelsPool.in(label)
        })
        this._activeLabels = []
        this._activeTotalWin = 0
        this._drawnCombinations = []
        this._isLineByLineSuspendedForBigWin = true
        ;(GameSignals as any).lineByLinePause?.emit()
    }

    private startLocalLineByLineLoop = () => {
        if (this._isLineByLineSuspendedForBigWin || !this._lineByLineCombinations.length) {
            return
        }

        this.skipTotalWinPresentation()

        //## To Loop Winlines Sound Enable the commented code.
        // if (this._lineByLineIndex === 0) {
        //     this._lineByLineSoundPlayedLines.clear()
        // }

        const combination = this._lineByLineCombinations[this._lineByLineIndex]

        this.clearActiveLines()
        this._drawnCombinations = combination ? [combination] : []

        if (combination) {
            this.drawLineForCombination(combination)

            // Symbol wins first, then label (label work must not delay the synced start).
            ;(GameSignals as any).lineByLineStepChanged?.emit({ combination })

            this.showLineByLineLabel(combination)

            const lineIdentifier = this.getLineByLineIdentifier(combination)

            if (!this._lineByLineSoundPlayedLines.has(lineIdentifier)) {
                this._lineByLineSoundPlayedLines.add(lineIdentifier)
                ;(GameSignals as any).lineByLineWinStep?.emit(combination)
            }
        }

        this.clearLineByLineTimer()
        this._lineByLineTimer = setTimeout(this.beginLineByLinePause, ShowWinnings.LINE_BY_LINE_SHOW_MS)
    }

    /** Clear payline + win anims, wait, then next payline. */
    private beginLineByLinePause = () => {
        if (this._isLineByLineSuspendedForBigWin || !this._lineByLineCombinations.length) {
            return
        }

        this.clearActiveLines()
        this._drawnCombinations = []
        this.hideLineByLineLabelForPause()
        ;(GameSignals as any).lineByLinePause?.emit()

        this._lineByLineIndex = (this._lineByLineIndex + 1) % this._lineByLineCombinations.length

        this.clearLineByLineTimer()
        this._lineByLineTimer = setTimeout(this.startLocalLineByLineLoop, ShowWinnings.LINE_BY_LINE_PAUSE_MS)
    }

    private hideLineByLineLabelForPause(): void {
        if (!this._lineByLineLabel) {
            return
        }

        gsap.killTweensOf(this._lineByLineLabel)
        this._lineByLineLabel.visible = false
        this._lineByLineLabel.alpha = 1
    }

    private onStopLineByLine = () => {
        const wasLoopRunning = this._isNativeLineByLineActive || this._isLocalLineByLineActive

        this.clearLineByLineTimer()
        this._isShowingDebugLines = false
        this._isNativeLineByLineActive = false
        this._isLocalLineByLineActive = false
        this._isLineByLineSuspendedForBigWin = false
        this._lineByLineCombinations = []
        this._lineByLineIndex = 0
        this._lineByLineSoundPlayedLines.clear()
        this._drawnCombinations = []
        this.hideLineByLineLabel()
        this.clearActiveLines()

        //## Wired to startSpin, so this runs on every spin — stay quiet unless a loop was running.
        if (wasLoopRunning) {
            ;(GameSignals as any).lineByLineLoopStopped?.emit()
        }
    }

    private getLineByLineIdentifier(combination: WinCombination): string {
        const paylineNumber = Number(combination?.payline_number || 0)
        if (Number.isFinite(paylineNumber) && paylineNumber > 0) {
            return `payline:${paylineNumber}`
        }

        const rawPoints = (
            Array.isArray(combination?.payline_path) && combination.payline_path.length >= 2
                ? combination.payline_path
                : combination?.symbols_positions || []
        ) as number[][]

        const fullPath = this.expandToFullPaylinePath(rawPoints, combination)

        const normalizedPositions = fullPath
            .filter((position) => Array.isArray(position) && position.length >= 2)
            .map((position) => `${Number(position[0])}:${Number(position[1])}`)
            .sort()
            .join('|')

        return `positions:${normalizedPositions}`
    }

    private onSkipLineByLine = () => {
        // Skip fast-forwards the current total-win banner only.
        // Payline rotation is dismissed by startSpin / bet change / feature intro, not by skip.
        this.skipTotalWinPresentation()
    }

    private skipTotalWinPresentation(): void {
        if (!this._activeLabels.length) {
            return
        }

        gsap.killTweensOf(this)
        const activeLabels = this._activeLabels.splice(0)
        const totalWin = this._activeTotalWin
        this._activeTotalWin = 0

        activeLabels.forEach((label: WinLabel) => {
            gsap.killTweensOf(label)
            label.countUpValue(totalWin, 0)
            label.showLabel()
            label.hideLabel(() => WinLabelsPool.in(label), 0.3)
        })

        this.clearActiveLines()
        this._drawnCombinations = []
    }

    private onBetChanged = () => {
        // Stake change must interrupt any line-win presentation / looping paylines.
        this.onStopLineByLine()
        gsap.killTweensOf(this)
        this._activeLabels.forEach((label: WinLabel) => {
            label.hideLabel(() => {
                WinLabelsPool.in(label)
            })
        })
        this._activeLabels = []
        this._drawnCombinations = []
        this.clearActiveLines()
        this.alpha = 1
    }

    private clearLineByLineTimer = () => {
        if (this._lineByLineTimer) {
            clearTimeout(this._lineByLineTimer)
            this._lineByLineTimer = null
        }
    }

    private getPaylineOriginX(): number {
        return 0
    }

    private getPaylineOriginY(): number {
        return 0
    }

    private createDebugControls(): void {
        const panel = new PIXI.Container()
        panel.position.set(8, 8)

        const background = new PIXI.Graphics()
        background.roundRect(0, 0, 340, 130, 8)
        background.fill({ color: 0x000000, alpha: 0.55 })
        ;(panel as any).addChild(background as any)

        this._debugControls = panel
        this._debugOffsetText = new PIXI.Text({
            text: 'Offset X: 0 | Y: 0',
            style: {
                fontSize: 14,
                fill: 0xffffff,
            },
        })
        this._debugOffsetText.position.set(10, 58)
        ;(panel as any).addChild(this._debugOffsetText as any)

        this._debugKeywordText = new PIXI.Text({
            text: 'KW: ALL',
            style: {
                fontSize: 13,
                fill: 0xfff59d,
            },
        })
        this._debugKeywordText.position.set(10, 106)
        ;(panel as any).addChild(this._debugKeywordText as any)

        panel.addChild(this.createDebugButton('Mostra 20 linee', 10, 10, 120, () => this.toggleAllDebugLines()))
        panel.addChild(
            this.createDebugButton('X-', 140, 10, 42, () => this.updateDebugOffset(-ShowWinnings.DEBUG_OFFSET_STEP, 0))
        )
        panel.addChild(
            this.createDebugButton('X+', 188, 10, 42, () => this.updateDebugOffset(ShowWinnings.DEBUG_OFFSET_STEP, 0))
        )
        panel.addChild(
            this.createDebugButton('Y-', 236, 10, 42, () => this.updateDebugOffset(0, -ShowWinnings.DEBUG_OFFSET_STEP))
        )
        panel.addChild(
            this.createDebugButton('Y+', 284, 10, 42, () => this.updateDebugOffset(0, ShowWinnings.DEBUG_OFFSET_STEP))
        )
        panel.addChild(this.createDebugButton('Reset', 10, 58, 70, () => this.resetDebugOffset()))
        panel.addChild(this.createDebugButton('ALL', 86, 58, 50, () => this.applyDebugKeyword('ALL')))
        panel.addChild(this.createDebugButton('L-', 142, 58, 40, () => this.showPrevDebugLine()))
        panel.addChild(this.createDebugButton('L+', 188, 58, 40, () => this.showNextDebugLine()))
        panel.addChild(this.createDebugButton('SOLO', 236, 58, 90, () => this.showCurrentDebugLine()))
        ;(this as any).addChild(panel as any)
    }

    private createDebugButton(label: string, x: number, y: number, width: number, onTap: () => void): PIXI.Container {
        const button = new PIXI.Container()
        button.position.set(x, y)

        const bg = new PIXI.Graphics()
        bg.roundRect(0, 0, width, 30, 6)
        bg.fill({ color: 0x1f2430, alpha: 0.9 })
        ;(button as any).addChild(bg as any)

        const text = new PIXI.Text({
            text: label,
            style: {
                fontSize: 12,
                fill: 0xffffff,
            },
        })
        text.anchor.set(0.5)
        text.position.set(width / 2, 15)
        ;(button as any).addChild(text as any)

        button.eventMode = 'static'
        button.cursor = 'pointer'
        button.on('pointertap', onTap)

        return button
    }

    private toggleAllDebugLines(): void {
        this.clearLineByLineTimer()
        if (this._isShowingDebugLines) {
            this._isShowingDebugLines = false
            this.clearActiveLines()
            this.refreshDebugKeywordText()
            return
        }

        this._isShowingDebugLines = true
        this._debugActiveLineNumber = 0
        this.renderDebugLines()
        this.refreshDebugKeywordText()
    }

    private drawStaticDebugLine(rawPoints: number[][]): Payline | null {
        if (rawPoints.length < 2) {
            return null
        }

        this.applyCurrentMeshOffset()

        const ropePoints = Payline.buildRopePoints(
            rawPoints,
            ReelsContainerHelper.calculateReelWidth(),
            ReelsContainerHelper.calculateReelHeight()
        )
        const orientedRopePoints = this.applyOrientationPathScale(ropePoints)

        const line = new Payline(orientedRopePoints)
        line.x = this.getPaylineOriginX()
        line.y = this.getPaylineOriginY()
        ;(this as any).addChildAt(line as any, 0)
        line.showStatic()

        return line
    }

    private updateDebugOffset(deltaX: number, deltaY: number): void {
        this._debugOffsetX += deltaX
        this._debugOffsetY += deltaY
        this.applyCurrentMeshOffset()
        this.refreshDebugOffsetText()

        if (this._isShowingDebugLines) {
            this.renderDebugLines()
        }
    }

    private resetDebugOffset(): void {
        this._debugOffsetX = ShowWinnings.DEFAULT_DEBUG_OFFSET_X
        this._debugOffsetY = ShowWinnings.DEFAULT_DEBUG_OFFSET_Y
        this.applyCurrentMeshOffset()
        this.refreshDebugOffsetText()

        if (this._isShowingDebugLines) {
            this.renderDebugLines()
        }
    }

    private refreshDebugOffsetText(): void {
        if (!this._debugOffsetText) {
            return
        }

        this._debugOffsetText.text = `Offset X: ${this._debugOffsetX} | Y: ${this._debugOffsetY}`
    }

    private applyCurrentMeshOffset(): void {
        Payline.setMeshOffset(
            this.getCurrentMeshBaseOffsetX() + this._debugOffsetX,
            this.getCurrentMeshBaseOffsetY() + this._debugOffsetY
        )
    }

    private onOrientationChanged = () => {
        this.rebuildVisiblePaylinesForOrientation()
        this.schedulePaylineLayoutSync()
    }

    private schedulePaylineLayoutSync(): void {
        this._paylineLayoutSyncCall?.kill()
        this._paylineLayoutSyncCall = gsap.delayedCall(ShowWinnings.PAYLINE_LAYOUT_SETTLE_S, () => {
            this._paylineLayoutSyncCall = null
            this.rebuildVisiblePaylinesForOrientation()
        })
    }

    private rebuildVisiblePaylinesForOrientation(): void {
        this.applyCurrentMeshOffset()

        if (this._isShowingDebugLines) {
            this._appliedPaylineOrientation = String(Facade.orientation || '')
            this.renderDebugLines()
            return
        }

        if (this._isLineByLineSuspendedForBigWin) {
            return
        }

        const orientation = String(Facade.orientation || '')
        const hasVisibleLines = this._activeLines.length > 0
        const hasVisibleLabels = this._activeLabels.length > 0 || !!this._lineByLineLabel?.visible

        if (!hasVisibleLines && !hasVisibleLabels) {
            this._appliedPaylineOrientation = orientation
            return
        }

        if (this._appliedPaylineOrientation === orientation) {
            this.repositionVisibleWinLabels()
            return
        }

        if (hasVisibleLines) {
            const combinations = this._drawnCombinations.length ? this._drawnCombinations : this._lineByLineCombinations
            this.clearActiveLines()
            combinations.forEach((combination) => {
                this.drawLineForCombination(combination, true)
            })
        }

        this.repositionVisibleWinLabels()
        this._appliedPaylineOrientation = orientation
    }

    private repositionVisibleWinLabels(): void {
        if (this._lineByLineLabel?.visible) {
            const combination = this._drawnCombinations[0] || this._lineByLineCombinations[this._lineByLineIndex]
            if (combination) {
                const labelPosition = this.getLabelPosition(combination)
                this._lineByLineLabel.position.set(labelPosition.x, labelPosition.y)
            }
        }

        if (!this._activeLabels.length) {
            return
        }

        const centerX = ReelsContainerHelper.calculateContainerWidth() / 2
        const centerY = ReelsContainerHelper.calculateContainerHeight() / 2
        this._activeLabels.forEach((label) => {
            label.position.set(centerX, centerY)
        })
    }

    private createDebugLineNumbers(reelCount: number): void {
        this.clearDebugLineNumbers()
        const referenceLines = this.getReferencePaylines()

        const indices =
            this._debugActiveLineNumber > 0
                ? [this._debugActiveLineNumber - 1]
                : referenceLines.map((_line, index) => index)

        indices.forEach((index) => {
            const lineDef = referenceLines[index]
            const firstRow = Number(lineDef[0])
            if (!Number.isFinite(firstRow)) {
                return
            }

            const text = new PIXI.Text({
                text: String(index + 1),
                style: {
                    fontSize: 14,
                    fontWeight: '700',
                    fill: 0xfff59d,
                    stroke: { color: 0x1a1a1a, width: 3 },
                },
            })

            const safeReels = Math.max(Number(reelCount || 0), 1)
            const leftPadding = safeReels > 0 ? 18 : 0

            text.anchor.set(1, 0.5)
            text.position.set(
                this.getCurrentMeshBaseOffsetX() + this._debugOffsetX - leftPadding,
                this.getCurrentMeshBaseOffsetY() +
                    this._debugOffsetY +
                    firstRow * ReelsContainerHelper.calculateReelHeight()
            )
            ;(this as any).addChild(text as any)
            this._debugLineNumberLabels.push(text)
        })
    }

    private getReferencePaylines(): number[][] {
        return ShowWinnings.DEBUG_PAYLINES_20
    }

    private renderDebugLines(): void {
        this.clearActiveLines()

        if (!this._isShowingDebugLines) {
            return
        }

        const reelCount = Number(Facade.reelsConfig.TOTAL_REELS || 5)
        const referenceLines = this.getReferencePaylines()
        const indices =
            this._debugActiveLineNumber > 0
                ? [this._debugActiveLineNumber - 1]
                : referenceLines.map((_line, index) => index)

        indices.forEach((index) => {
            const lineDef = referenceLines[index]
            if (!Array.isArray(lineDef)) {
                return
            }

            const points = lineDef.slice(0, reelCount).map((row, reel) => [reel, row])
            const line = this.drawStaticDebugLine(points)
            if (line) {
                this._activeLines.push(line)
            }
        })

        this.createDebugLineNumbers(reelCount)
    }

    private applyDebugKeyword(keyword: string): void {
        const normalized = String(keyword || '')
            .trim()
            .toUpperCase()

        if (!normalized || normalized === 'ALL') {
            this._debugActiveLineNumber = 0
            this._isShowingDebugLines = true
            this.renderDebugLines()
            this.refreshDebugKeywordText()
            return
        }

        const match = normalized.match(/^L(\d+)$/)
        if (!match) {
            return
        }

        const total = this.getReferencePaylines().length
        const lineNumber = Number(match[1])

        if (!Number.isFinite(lineNumber) || lineNumber < 1 || lineNumber > total) {
            return
        }

        this._debugActiveLineNumber = lineNumber
        this._isShowingDebugLines = true
        this.renderDebugLines()
        this.refreshDebugKeywordText()
    }

    private showPrevDebugLine(): void {
        const total = this.getReferencePaylines().length
        if (!total) {
            return
        }

        const current = this._debugActiveLineNumber > 0 ? this._debugActiveLineNumber : 1
        const next = ((current - 2 + total) % total) + 1
        this.applyDebugKeyword(`L${next}`)
    }

    private showNextDebugLine(): void {
        const total = this.getReferencePaylines().length
        if (!total) {
            return
        }

        const current = this._debugActiveLineNumber > 0 ? this._debugActiveLineNumber : 0
        const next = (current % total) + 1
        this.applyDebugKeyword(`L${next}`)
    }

    private showCurrentDebugLine(): void {
        if (this._debugActiveLineNumber > 0) {
            this.applyDebugKeyword(`L${this._debugActiveLineNumber}`)
            return
        }

        this.applyDebugKeyword('L1')
    }

    private refreshDebugKeywordText(): void {
        if (!this._debugKeywordText) {
            return
        }

        this._debugKeywordText.text =
            this._debugActiveLineNumber > 0 ? `KW: L${this._debugActiveLineNumber}` : 'KW: ALL'
    }

    private getCurrentMeshBaseOffsetX(): number {
        return Facade.orientation === Orientation.PORTRAIT
            ? ShowWinnings.PAYLINE_MESH_BASE_OFFSET_X_PORTRAIT
            : ShowWinnings.PAYLINE_MESH_BASE_OFFSET_X_LANDSCAPE
    }

    private getCurrentMeshBaseOffsetY(): number {
        return Facade.orientation === Orientation.PORTRAIT
            ? ShowWinnings.PAYLINE_MESH_BASE_OFFSET_Y_PORTRAIT
            : ShowWinnings.PAYLINE_MESH_BASE_OFFSET_Y_LANDSCAPE
    }

    private getCurrentPathScaleX(): number {
        return Facade.orientation === Orientation.PORTRAIT
            ? ShowWinnings.PAYLINE_PATH_SCALE_X_PORTRAIT
            : ShowWinnings.PAYLINE_PATH_SCALE_X_LANDSCAPE
    }

    private getCurrentPathScaleY(): number {
        return Facade.orientation === Orientation.PORTRAIT
            ? ShowWinnings.PAYLINE_PATH_SCALE_Y_PORTRAIT
            : ShowWinnings.PAYLINE_PATH_SCALE_Y_LANDSCAPE
    }

    private applyOrientationPathScale(points: PIXI.Point[]): PIXI.Point[] {
        const scaleX = this.getCurrentPathScaleX()
        const scaleY = this.getCurrentPathScaleY()

        if (scaleX === 1 && scaleY === 1) {
            return points
        }

        const centerX =
            this.getCurrentMeshBaseOffsetX() + this._debugOffsetX + ReelsContainerHelper.calculateContainerWidth() / 2
        const centerY =
            this.getCurrentMeshBaseOffsetY() + this._debugOffsetY + ReelsContainerHelper.calculateContainerHeight() / 2

        return points.map((point) => {
            return new PIXI.Point(centerX + (point.x - centerX) * scaleX, centerY + (point.y - centerY) * scaleY)
        })
    }

    private getPositionX(reelIndex: number): number {
        return ReelsContainerHelper.calculateReelWidth() * reelIndex + Facade.symbolsConfig.WIDTH / 2
    }

    private getPositionY(rowIndex: number): number {
        return ReelsContainerHelper.calculateReelHeight() * rowIndex + Facade.symbolsConfig.HEIGHT / 2
    }

    private clearActiveLines(): void {
        this._activeLines.forEach((line) => {
            line.hide()
            if (line.parent) {
                line.parent.removeChild(line)
            }
            line.destroy()
        })

        this._activeLines = []
        this.clearDebugLineNumbers()
    }

    private showLineByLineLabel(combination: WinCombination): void {
        if (!combination || !combination.win_currency) {
            return
        }

        // Reuse one label for the whole line-by-line loop (Spine.from every step was desyncing wins).
        const label = this._lineByLineLabel || WinLabelsPool.out(this)
        this._lineByLineLabel = label

        if (!label.parent) {
            this.addChild(label)
        }

        const labelPosition = this.getLabelPosition(combination)
        label.position.set(labelPosition.x, labelPosition.y)
        label.showLineWin(combination.win_currency)
    }

    private hideLineByLineLabel(onComplete?: () => void): void {
        if (!this._lineByLineLabel) {
            if (onComplete) {
                onComplete()
            }

            return
        }

        const label = this._lineByLineLabel
        this._lineByLineLabel = null

        label.hideLabel(() => {
            if (label.parent) {
                ;(label.parent as any).removeChild(label as any)
            }
            WinLabelsPool.in(label)

            if (onComplete) {
                onComplete()
            }
        })
    }

    private clearDebugLineNumbers(): void {
        this._debugLineNumberLabels.forEach((label) => {
            if (label.parent) {
                ;(label.parent as any).removeChild(label as any)
            }
            label.destroy()
        })

        this._debugLineNumberLabels = []
    }

    private getLabelPosition = (combination: WinCombination): { x: number; y: number } => {
        const winningPositions = (combination?.symbols_positions || [])
            .filter((position): position is number[] => {
                return (
                    Array.isArray(position) &&
                    position.length >= 2 &&
                    Number.isFinite(Number(position[0])) &&
                    Number.isFinite(Number(position[1]))
                )
            })
            .map((position) => [Number(position[0]), Number(position[1])])
            .sort((a, b) => a[0] - b[0] || a[1] - b[1])

        if (!winningPositions.length) {
            return {
                x: ReelsContainerHelper.calculateContainerWidth() / 2,
                y: ReelsContainerHelper.calculateContainerHeight() / 2,
            }
        }

        // Place the popup on the winning symbol occupying the grid's central reel.
        // Example: for A A A _ _, use the third A (reel 2), not the second A.
        const centerReel = (Math.max(Number(Facade.reelsConfig.TOTAL_REELS || 5), 1) - 1) / 2
        const centerPosition = winningPositions.reduce((closest, candidate) => {
            const closestDistance = Math.abs(closest[0] - centerReel)
            const candidateDistance = Math.abs(candidate[0] - centerReel)

            return candidateDistance < closestDistance ? candidate : closest
        })

        return {
            x: this.getPositionX(centerPosition[0]),
            y: this.getPositionY(centerPosition[1]),
        }
    }
}
